import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateRangePicker } from '@mui/x-date-pickers-pro';
import type { DateRange } from '@mui/x-date-pickers-pro';
import dayjs, { type Dayjs } from 'dayjs';
import { semantic } from '@diligentcorp/atlas-design-tokens/lens';
import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import { Button } from 'src/components/atoms';
import {
  DisclosureFilterColumn,
  DisclosureFilterValue,
  FilterOptionsByColumn,
} from 'src/components/pages/CoiDisclosures/types';
import * as styles from './DisclosuresFilterPopover.styles';

const COLUMN_OPTIONS_ALL: { value: DisclosureFilterColumn; label: string }[] = [
  { value: 'category', label: 'Category' },
  { value: 'status', label: 'Status' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'submittedDate', label: 'Submission Date' },
  { value: 'formTitle', label: 'Form Title' },
];

type DropdownColumn = 'category' | 'status' | 'reviewer';
type TextColumn = 'formTitle';

export type FilterLogic = 'and' | 'or';

export interface FilterRow {
  id: string;
  column: DisclosureFilterColumn | '';
  logic?: FilterLogic;
}

const LOGIC_OPTIONS: { value: FilterLogic; label: string }[] = [
  { value: 'and', label: 'And' },
  { value: 'or', label: 'Or' },
];

export interface DisclosuresFilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  rows: FilterRow[];
  onRowsChange: (rows: FilterRow[]) => void;
  value: DisclosureFilterValue;
  onChange: (value: DisclosureFilterValue) => void;
  onRemoveAll: () => void;
  makeId: () => string;
  filterOptions: FilterOptionsByColumn;
  visibleColumns: DisclosureFilterColumn[];
}

export const DisclosuresFilterPopover: React.FC<
  DisclosuresFilterPopoverProps
> = ({
  anchorEl,
  open,
  onClose,
  rows,
  onRowsChange,
  value,
  onChange,
  onRemoveAll,
  makeId,
  filterOptions,
  visibleColumns,
}) => {
  const COLUMN_OPTIONS = useMemo(
    () =>
      COLUMN_OPTIONS_ALL.filter((col) => visibleColumns.includes(col.value)),
    [visibleColumns],
  );

  const chosenColumns = useMemo(
    () => rows.map((r) => r.column).filter(Boolean),
    [rows],
  );

  const handleColumnChange = (
    rowId: string,
    nextColumn: DisclosureFilterColumn,
  ) => {
    const prevColumn = rows.find((r) => r.id === rowId)?.column;
    if (prevColumn && prevColumn !== nextColumn) {
      onChange(clearColumn(value, prevColumn));
    }
    onRowsChange(
      rows.map((r) => (r.id === rowId ? { ...r, column: nextColumn } : r)),
    );
  };

  const handleTextValueChange = (column: TextColumn, next: string) => {
    onChange({ ...value, [column]: next ? [next] : [] });
  };

  const handleDropdownChange = (column: DropdownColumn, next: string) => {
    onChange({ ...value, [column]: next ? [next] : [] });
  };

  const handleDateRangeChange = (range: DateRange<Dayjs>) => {
    const [from, to] = range;
    if (!from && !to) {
      const next = { ...value };
      delete next.submittedDate;
      onChange(next);
      return;
    }
    onChange({
      ...value,
      submittedDate: {
        from: from ? from.format('YYYY-MM-DD') : '',
        to: to ? to.format('YYYY-MM-DD') : '',
      },
    });
  };

  const handleLogicChange = (rowId: string, logic: FilterLogic) => {
    onRowsChange(rows.map((r) => (r.id === rowId ? { ...r, logic } : r)));
  };

  const handleAddFilter = () => {
    onRowsChange([...rows, { id: makeId(), column: '', logic: 'and' }]);
  };

  const handleRemoveRow = (rowId: string) => {
    const removed = rows.find((r) => r.id === rowId)?.column;
    const nextRows = rows.filter((r) => r.id !== rowId);
    onRowsChange(nextRows);
    if (removed) onChange(clearColumn(value, removed));
  };

  const renderValueControl = (row: FilterRow) => {
    if (!row.column) {
      return (
        <TextField
          size="small"
          disabled
          label="Value"
          placeholder="Filter value"
          sx={styles.field}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'Filter value' }}
        />
      );
    }

    if (row.column === 'submittedDate') {
      const range = value.submittedDate;
      const dateRangeValue: DateRange<Dayjs> = [
        range?.from ? dayjs(range.from) : null,
        range?.to ? dayjs(range.to) : null,
      ];
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDateRangePicker
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            slotProps={{
              textField: ({ position }) => ({
                size: 'small',
                inputProps: {
                  'data-testid':
                    position === 'start'
                      ? 'disclosures-filter-date-from'
                      : 'disclosures-filter-date-to',
                },
              }),
              fieldSeparator: { children: '–' },
              day: {
                sx: {
                  '& .MuiPickersDay-root.Mui-selected': {
                    backgroundColor:
                      semantic.color.action.destructive.default.value,
                  },
                  '& .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus':
                    {
                      backgroundColor:
                        semantic.color.action.destructive.hover.value,
                    },
                },
              },
            }}
          />
        </LocalizationProvider>
      );
    }

    if (row.column === 'formTitle') {
      const text = value.formTitle?.[0] ?? '';
      return (
        <TextField
          size="small"
          label="Value"
          placeholder="Filter value"
          value={text}
          onChange={(e) => handleTextValueChange('formTitle', e.target.value)}
          sx={styles.field}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'disclosures-filter-value-formTitle' }}
        />
      );
    }

    const column = row.column as DropdownColumn;
    const options = filterOptions[column] ?? [];
    const selectedValue = value[column]?.[0] ?? '';

    return (
      <FormControl size="small" sx={styles.field}>
        <InputLabel shrink>Value</InputLabel>
        <Select
          displayEmpty
          label="Value"
          value={selectedValue}
          onChange={(e) => handleDropdownChange(column, e.target.value)}
          inputProps={{ 'aria-label': `Filter value for ${column}` }}
          SelectDisplayProps={
            {
              'data-testid': `disclosures-filter-value-${column}`,
            } as React.HTMLAttributes<HTMLDivElement>
          }
          renderValue={(selected) =>
            selected ? (
              (options.find((o) => o.value === selected)?.label ?? selected)
            ) : (
              <Typography component="span" sx={styles.placeholderText}>
                Select a value
              </Typography>
            )
          }
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: styles.popoverPaper } }}
    >
      <Box sx={styles.container} data-testid="disclosures-filter-popover">
        <Stack spacing={1.5}>
          {rows.map((row, index) => (
            <Stack
              key={row.id}
              direction="row"
              spacing={1.5}
              sx={styles.row}
              data-testid="disclosures-filter-row"
            >
              <IconButton
                aria-label="Remove filter"
                size="small"
                disabled={rows.length <= 1}
                onClick={() => handleRemoveRow(row.id)}
                data-testid="disclosures-filter-remove-row"
                sx={styles.removeIcon}
              >
                <CloseIcon size="md" />
              </IconButton>

              {index === 0 ? (
                <Box sx={styles.logicControl} />
              ) : (
                <FormControl size="small" sx={styles.logicControl}>
                  <Select
                    value={row.logic ?? 'and'}
                    onChange={(e) =>
                      handleLogicChange(row.id, e.target.value as FilterLogic)
                    }
                    inputProps={{
                      'aria-label': 'Logic operator',
                      'data-testid': 'disclosures-filter-logic',
                    }}
                  >
                    {LOGIC_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={styles.field}>
                <InputLabel shrink>Columns</InputLabel>
                <Select
                  displayEmpty
                  label="Columns"
                  value={row.column}
                  onChange={(e) =>
                    handleColumnChange(
                      row.id,
                      e.target.value as DisclosureFilterColumn,
                    )
                  }
                  inputProps={{ 'aria-label': 'Choose a column' }}
                  renderValue={(selected) =>
                    selected ? (
                      COLUMN_OPTIONS.find((c) => c.value === selected)?.label
                    ) : (
                      <Typography component="span" sx={styles.placeholderText}>
                        Choose a column
                      </Typography>
                    )
                  }
                >
                  {COLUMN_OPTIONS.map((col) => (
                    <MenuItem
                      key={col.value}
                      value={col.value}
                      disabled={
                        col.value !== row.column &&
                        chosenColumns.includes(col.value)
                      }
                    >
                      {col.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {renderValueControl(row)}
            </Stack>
          ))}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={styles.footer}
        >
          <Button
            variant="tertiary"
            onClick={handleAddFilter}
            data-testid="disclosures-filter-add"
            style={styles.footerButton}
          >
            <AddIcon size="md" style={styles.footerButtonIcon} />
            Add filter
          </Button>
          <Button
            variant="tertiary"
            onClick={onRemoveAll}
            data-testid="disclosures-filter-remove-all"
            style={styles.footerButton}
          >
            <TrashIcon size="md" style={styles.footerButtonIcon} />
            Remove all
          </Button>
        </Stack>
      </Box>
    </Popover>
  );
};

function clearColumn(
  value: DisclosureFilterValue,
  column: DisclosureFilterColumn,
): DisclosureFilterValue {
  if (column === 'submittedDate') {
    const next = { ...value };
    delete next.submittedDate;
    return next;
  }
  return { ...value, [column]: [] };
}
